import { Component, Input, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatGridListModule } from '@angular/material/grid-list';
import { CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { File as AwareFile, Unit } from '@appbolaget/aware-model';
import { SecureImagePipe } from '@helpers/pipes';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { PreviewLocalImagePipe } from '@components/media/pipes/preview-local-image.pipe';
import { MatMenuModule } from '@angular/material/menu';
import { AwareMediaUploadSource } from '@components/media/symbols';
import { NgxUploaderModule, UploadOutput } from 'ngx-uploader';
import { reorderArray } from '@appbolaget/helpers';
import { MediaDialogService } from '@components/media/media-dialog.service';
import { FileUploaderService } from '@services/http';
import { FileIconPipe } from '@components/media/pipes/file-icon.pipe';
import { TranslateModule } from '@ngx-translate/core';

export type FileBridgeLocalFile = {
    file: File;
    mime: string;
    isImage: boolean;
    isVideo: boolean;
    isLocalFile: true;
};
export type FileBridgeAllFiles = (AwareFile | FileBridgeLocalFile)[];
@Component({
    selector: 'aw-file-bridge',
    templateUrl: 'file-bridge.component.html',
    styleUrls: ['./file-bridge.component.scss'],
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => FileBridgeComponent),
        },
    ],
    imports: [
        MatGridListModule,
        CdkDropList,
        CdkDrag,
        SecureImagePipe,
        MatTooltipModule,
        MatButtonModule,
        MatIconModule,
        NgIf,
        NgFor,
        PreviewLocalImagePipe,
        MatMenuModule,
        NgxUploaderModule,
        FileIconPipe,
        TranslateModule,
    ],
})
export class FileBridgeComponent implements ControlValueAccessor {
    @Input() allowDirectUpload: boolean;
    @Input() unit: Unit;
    @Input() cols = 6;

    onChange = (_) => {};
    onTouched = () => {};
    disabled: boolean;

    files: FileBridgeAllFiles;
    sources = AwareMediaUploadSource;

    #mediaDialogService = inject(MediaDialogService);
    #fileUploaderService = inject(FileUploaderService);

    addFromSource(source: AwareMediaUploadSource) {
        if (source === AwareMediaUploadSource.library) {
            this.#addFromLibrary();
        } else if (source === AwareMediaUploadSource.direct) {
            this.#addFromDirect();
        }
    }

    onUploadOutput(output: UploadOutput) {
        if (output.type === 'addedToQueue' && typeof output.file !== 'undefined') {
            this.files = [
                ...this.files,
                {
                    file: output.file.nativeFile,
                    mime: output.file.type,
                    isImage: output.file.type.includes('image'),
                    isVideo: output.file.type.includes('video'),
                    isLocalFile: true,
                },
            ];

            this.onChange(this.files);
        }
    }

    remove(index: number) {
        const file = this.files[index];
        if (file instanceof AwareFile && this.allowDirectUpload) {
            file.localProps = {
                toDelete: file.localProps?.toDelete ? !file.localProps.toDelete : true,
            };
        } else {
            this.files.splice(index, 1);
        }

        this.onChange(this.files);
    }

    sortDrop($event) {
        this.files = reorderArray({ newIndex: $event.currentIndex, oldIndex: $event.previousIndex }, this.files);
        this.onChange(this.files);
    }

    writeValue(files: FileBridgeAllFiles): void {
        this.files = files;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    async #addFromDirect() {
        const files = await this.#fileUploaderService.openFileSelector(true);
        const mappedFiles: FileBridgeLocalFile[] = files.map((file) => ({
            file,
            mime: file.type,
            isImage: file.type.includes('image'),
            isVideo: file.type.includes('video'),
            isLocalFile: true,
        }));

        this.files = [...this.files, ...mappedFiles];
        this.onChange(this.files);
    }

    async #addFromLibrary() {
        let options;

        if (this.unit && this.unit instanceof Unit) {
            options = {
                unit: this.unit,
            };
        }

        const dialog = await this.#mediaDialogService.open(true, options);

        dialog.afterClosed().subscribe((data) => {
            if (Array.isArray(data)) {
                this.files = [...(this.files || []), ...data];

                this.onChange(this.files);
            }
        });
    }
}

// import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
// import { reorderArray } from '@appbolaget/helpers';
// import { MediaDialogService } from '../../media-dialog.service';
// import { UntypedFormControl } from '@angular/forms';
// import { Unit, File as AwareFile } from '@appbolaget/aware-model';
// import { NgArrayPipesModule } from 'ngx-pipes';
// import { FilePrimaryImageComponent } from '../file-primary-image/file-primary-image.component';
// import { MatIconModule } from '@angular/material/icon';
// import { MatButtonModule } from '@angular/material/button';
// import { MatTooltipModule } from '@angular/material/tooltip';
// import { NgFor, NgIf } from '@angular/common';
// import { CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
// import { MatGridListModule } from '@angular/material/grid-list';
// import { SecureImagePipe } from '@helpers/pipes';
// import { MatMenuModule } from '@angular/material/menu';
// import { AwareMediaUploadSource } from '@components/media/symbols';
// import { NgxUploaderModule, UploadOutput } from 'ngx-uploader';
// import { PreviewLocalImagePipe } from '@components/media/pipes/preview-local-image.pipe';

// export type FileBridgeLocalFile = {
//     file: File;
//     mime: string;
//     isImage: boolean;
//     isVideo: boolean;
// };
// @Component({
//     selector: 'aw-file-bridge',
//     templateUrl: 'file-bridge.component.html',
//     styleUrls: ['./file-bridge.component.scss'],
//     standalone: true,
//     imports: [
//         MatGridListModule,
//         CdkDropList,
//         NgFor,
//         CdkDrag,
//         NgxUploaderModule,
//         MatTooltipModule,
//         MatButtonModule,
//         MatIconModule,
//         NgIf,
//         FilePrimaryImageComponent,
//         NgArrayPipesModule,
//         SecureImagePipe,
//         MatMenuModule,
//         PreviewLocalImagePipe,
//     ],
// })
// export class FileBridgeComponent implements OnInit {
//     _localFiles: FileBridgeLocalFile[] = []; // Used for direct upload
//     _files: AwareFile[]; // Used for library upload

//     @Input('files')
//     set files(files) {
//         this._files = (files ?? []).map((file, index) => {
//             file.position = index;

//             return file;
//         });
//     }

//     get files() {
//         return this._files;
//     }

//     @Input('localFiles')
//     set localFiles(files) {
//         this._localFiles = files;
//     }

//     get localFiles() {
//         return this._localFiles;
//     }

//     @Input()
//     primary: UntypedFormControl;

//     @Input()
//     unit: Unit;

//     @Input()
//     cols = 6;

//     @Input()
//     rules: {};

//     @Input()
//     allowDirectUpload: boolean;

//     @Output()
//     changed = new EventEmitter();

//     sources = AwareMediaUploadSource;

//     constructor(public mediaDialogService: MediaDialogService) {}

//     ngOnInit() {}

//     addFromSource(source: AwareMediaUploadSource = AwareMediaUploadSource.library) {
//         if (source === AwareMediaUploadSource.library) {
//             return this.add();
//         }
//     }

//     async add() {
//         let options;

//         if (this.unit && this.unit instanceof Unit) {
//             options = {
//                 unit: this.unit,
//                 rules: this.rules,
//             };
//         }

//         const dialog = await this.mediaDialogService.open(true, options);

//         dialog.afterClosed().subscribe((data) => {
//             if (Array.isArray(data)) {
//                 if (!this.files) {
//                     this.files = [];
//                 }

//                 const files = this.files.concat(data);

//                 this.changed.emit(files);
//             }
//         });
//     }

//     remove(_: AwareFile, index: number) {
//         this.files.splice(index, 1);

//         this.changed.emit(this.files);
//     }

//     sortDrop($event) {
//         console.log('drop', $event);
//         if ($event.item.data instanceof AwareFile) {
//             this.changed.emit(reorderArray({ newIndex: $event.currentIndex, oldIndex: $event.previousIndex }, this.files, true));
//         } else {
//             console.log('reorder local file');
//             // this.changed.emit(reorderArray({ newIndex: $event.currentIndex + this.files.length, oldIndex: $event.previousIndex + this.files.length }, this.localFiles));
//         }
//     }

//     sortPredicate(index: number, item: CdkDrag<AwareFile | FileBridgeLocalFile>) {
//         const awareFilesLength = this.files?.length ?? 0;
//         const localFilesLength = this.localFiles?.length ?? 0;
//         let minIndex = 0;
//         let maxIndex = 0;
//         if (item.data instanceof AwareFile) {
//             minIndex = 0;
//             maxIndex = awareFilesLength - 1;
//         } else {
//             minIndex = awareFilesLength + 2;
//             maxIndex = awareFilesLength + localFilesLength;
//         }

//         return index >= minIndex && index <= maxIndex;
//     }

//     onUploadOutput(output: UploadOutput): void {
//         console.log('uplload', output);
//         if (output.type === 'addedToQueue' && typeof output.file !== 'undefined') {
//             this.localFiles.push({
//                 file: output.file.nativeFile,
//                 mime: output.file.type,
//                 isImage: output.file.type.includes('image'),
//                 isVideo: output.file.type.includes('video'),
//             });
//         }
//     }
// }
